/**
 * FRONT-1412 review finding (bundle size): `isProposePlanSuccessShape` and
 * `planChipStatus`/`selectPlanStatus` used to live in `message-bubble.tsx`
 * and `propose-plan-card.tsx` respectively, and the phone's `pwa-message.tsx`
 * imported them straight from there — reusing the exact same pure logic, but
 * `message-bubble.tsx` alone is a 1798-line module with 34 imports that pulls
 * in cytoscape (the graph-visualization stack), `graph-view.tsx`,
 * `activity-feed.tsx` and friends. The `/m` phone entry shipped all of that
 * on its critical path as a result — a measured 4.7 KB -> 211.5 KB gzip
 * regression on the shared chunk it eagerly imports, for a 12-line helper.
 *
 * This module is the fix: the pure predicate and the pure status-mapping
 * helpers, on their own, dependency-free (no React, no icons, no query
 * hooks) — mirrors the `tool-signals.ts` precedent. `message-bubble.tsx` and
 * `propose-plan-card.tsx` re-export from here so their own behaviour, call
 * sites and tests are untouched; the phone imports this file directly
 * instead of either of them.
 */

/** Status → chip label/color mapping for the propose_plan transcript chip.
 *  Follows the one-green/one-red rule (CLAUDE.md): only 'completed' is
 *  success, only 'rejected' is destructive — everything else (including
 *  'approved', which means accepted-and-running, not done) is neutral. */
export function planChipStatus(
  status: string,
): { label: string; variant: 'secondary' | 'success-soft' | 'destructive-soft' } {
  switch (status) {
    case 'completed':
      return { label: 'Completed', variant: 'success-soft' };
    case 'rejected':
      return { label: 'Rejected', variant: 'destructive-soft' };
    case 'approved':
      return { label: 'Approved', variant: 'secondary' };
    case 'pending':
      return { label: 'Pending', variant: 'secondary' };
    case 'cancelled':
      return { label: 'Cancelled', variant: 'secondary' };
    case 'superseded':
      return { label: 'Superseded', variant: 'secondary' };
    default:
      return { label: 'Plan', variant: 'secondary' };
  }
}

/**
 * Picks the status a propose_plan chip should display, given the live
 * session plan (from `usePlan`), the id the tool call froze at propose time,
 * and the frozen output's own status.
 *
 * `livePlan === undefined` means the query hasn't resolved yet (loading, or
 * cold cache — `usePlan` sets `refetchOnMount: 'always'`, so this is true on
 * every fresh mount until the response lands). We must NOT guess
 * 'superseded' in that window — that was the bug: a full reload or first
 * navigation into a chat briefly rendered "Superseded" on every plan chip
 * before self-correcting a few hundred ms later. Only a RESOLVED query
 * (livePlan is `null` or a concrete plan) can tell us the frozen plan is no
 * longer current.
 *
 * FRONT-755 (desktop `ProposePlanCard`) fixed the underlying bug this
 * guards against: `propose_plan` always returns `{ status: "pending" }` at
 * call time (src/tools/plan.ts), so reading only the frozen output freezes
 * the chip at "Pending" forever, on every reload, for every historical plan.
 * FRONT-1412 review finding: the phone's chip had the same bug (it read only
 * the frozen output) until it started calling this too.
 */
export function selectPlanStatus(
  livePlan: { id: number; status: string } | null | undefined,
  frozenId: number | undefined,
  frozenStatus: string | undefined,
): string {
  // Authoritative: the live plan this chip proposed, matched by id.
  if (livePlan && frozenId != null && livePlan.id === frozenId) return livePlan.status;
  // Query not resolved yet — show the frozen status until the current plan
  // is actually known, rather than guessing.
  if (livePlan === undefined) return frozenStatus ?? 'pending';
  // Resolved (null, or a different current plan): a frozenId that doesn't
  // match the current plan means this plan was superseded by a newer one.
  if (frozenId != null) return 'superseded';
  // No id to match on → best-effort frozen status.
  return frozenStatus ?? 'pending';
}

/**
 * FRONT-804: `propose_plan`'s existing already-active-plan guard returns
 * `{ error, activePlan }` instead of the normal `{ status, plan }` shape when
 * the model redundantly re-calls it. A parser that only understands the
 * success shape would render a blank, title-less "Pending" chip duplicating
 * the real, already-active plan shown elsewhere. Exported (pure) so it's
 * unit-testable without rendering, same as `hasTopLevelError`/`hasNoChange`
 * in `message-bubble.tsx`.
 */
export function isProposePlanSuccessShape(output: unknown): boolean {
  // /code-review finding (2026-08-03): match hasTopLevelError/hasNoChange's
  // one-parse-layer convention. propose_plan's own handler never returns a
  // string today, so this is defense-in-depth for consistency, not a
  // currently-triggering path.
  let candidate: unknown = output;
  if (typeof candidate === 'string') {
    try {
      candidate = JSON.parse(candidate);
    } catch {
      return false;
    }
  }
  if (!candidate || typeof candidate !== 'object' || Array.isArray(candidate)) return false;
  const plan = (candidate as Record<string, unknown>).plan;
  return Boolean(plan && typeof plan === 'object' && (plan as Record<string, unknown>).id != null);
}
